import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useNavigationType, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { forexApi, type AiDecisionDTO } from '@/services/forexApi';
import { copyResultLabel, decisionPresentation, shortAgentLabel } from '@/lib/decisionPresentation';
import {
  AI_LIST_ROUTE, readDecisionListView, restoreDecisionListView, saveDecisionListView, validDecisionListView,
} from '@/lib/decisionListNavigation';

function formatDateTime(value?: string | number | null) {
  if (value == null || value === '') return null;
  const numeric = typeof value === 'number' ? value : /^\d{10,13}$/.test(value) ? Number(value) : null;
  const date = new Date(numeric === null ? value : numeric < 1e12 ? numeric * 1000 : numeric);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.toLocaleString('zh-CN', {
    timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  })}（UTC+8）`;
}

export default function DecisionsPage() {
  const { id } = useParams();
  const { accessToken, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get('page') || '1';
  const page = /^[1-9]\d{0,3}$/.test(pageParam) ? Number(pageParam) : 1;
  const requestKey = id ? `detail:${id}` : `list:${page}`;
  const [loadedFor, setLoadedFor] = useState('');
  const [items, setItems] = useState<AiDecisionDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [more, setMore] = useState(false);
  const requestSequence = useRef(0);
  const restored = useRef(false);
  const returnView = validDecisionListView(location.state?.decisionListReturn, user?.user_id);
  const busy = loading || loadedFor !== requestKey;
  const visibleItems = loadedFor === requestKey ? items : [];

  const load = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError('');
    setItems([]);
    try {
      if (id) {
        const item = await forexApi.getAiDecision(id, accessToken);
        if (sequence !== requestSequence.current) return;
        setItems([item]);
        setMore(false);
      } else {
        const data = await forexApi.listAiDecisions(accessToken, page, 20);
        if (sequence !== requestSequence.current) return;
        setItems(data.items);
        setMore(data.has_more);
      }
    } catch {
      if (sequence !== requestSequence.current) return;
      setError('决策记录暂时无法加载，请重试。');
    } finally {
      if (sequence === requestSequence.current) {
        setLoadedFor(requestKey);
        setLoading(false);
      }
    }
  }, [id, accessToken, page, requestKey]);

  useEffect(() => {
    void load();
    return () => { requestSequence.current += 1; };
  }, [load]);

  useEffect(() => {
    if (id || busy || error || restored.current) return;
    const view = validDecisionListView(location.state?.restoreDecisionList, user?.user_id)
      || (navigationType === 'POP' ? readDecisionListView(user?.user_id) : null);
    if (!view || (view.entryKey !== location.key && !location.state?.restoreDecisionList)
      || view.url !== `${location.pathname}${location.search}`) return;
    const frame = requestAnimationFrame(() => {
      restoreDecisionListView(view);
      restored.current = true;
    });
    return () => cancelAnimationFrame(frame);
  }, [id, busy, error, location, navigationType, user?.user_id]);

  function backToList() {
    if (returnView && returnView.historyIndex >= 0 && window.history.state?.idx === returnView.historyIndex + 1) {
      navigate(-1);
    } else {
      navigate(returnView?.url || AI_LIST_ROUTE, {
        replace: true, state: returnView ? { restoreDecisionList: returnView } : null,
      });
    }
  }

  function openDecision(decisionId: string) {
    const view = user ? saveDecisionListView(user.user_id, location.key, `${location.pathname}${location.search}`, decisionId) : null;
    restored.current = false;
    navigate(`/ai-decisions/${encodeURIComponent(decisionId)}`, { state: { decisionListReturn: view } });
  }

  return (
    <main className="min-h-screen bg-gray-50 px-5 pb-24 pt-5">
      <button type="button" onClick={backToList}
        className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-600 active:bg-indigo-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2">
        <i aria-hidden="true" className="ri-arrow-left-line text-base" />
        返回 AI 列表
      </button>
      <h1 className="mb-2 mt-5 text-xl font-bold text-gray-900">{id ? '决策详情' : 'AI 决策记录'}</h1>
      <p className="mb-5 text-sm leading-relaxed text-gray-500">查看本轮决策原因、共享策略信号与我的账户结果。</p>

      {busy && <p role="status" className="text-sm text-gray-500">正在加载决策记录…</p>}
      {!busy && error && (
        <div role="alert" className="rounded-xl border border-gray-200 bg-white p-4 text-sm">
          <p>{error}</p>
          <button onClick={() => void load()} className="mt-3 min-h-11 rounded-lg border border-gray-200 px-4 text-indigo-600">重试</button>
        </div>
      )}
      {!busy && !error && !visibleItems.length && <p className="text-sm text-gray-500">暂无决策记录。</p>}

      <div className="space-y-3">
        {visibleItems.map((item) => {
          const view = decisionPresentation(item);
          return (
            <article key={item.id} id={`decision-${item.id}`} className="rounded-2xl bg-white p-4 shadow-sm sm:p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-gray-900 sm:text-lg">{view.symbolLabel}{view.periodLabel ? ` · ${view.periodLabel}` : ''}</h2>
                  <p className="mt-2 text-xs text-gray-500">{shortAgentLabel(item.agent_id)}</p>
                  <p className="mt-1 text-xs leading-relaxed text-gray-500">行情截至：{formatDateTime(item.data_as_of) || '行情截止时间暂缺'}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${view.tone}`}>{view.label}</span>
              </div>
              {item.record_type === 'legacy_decision' && <p className="mt-2 text-xs text-gray-500">历史决策记录 · 仅展示可核实的结果</p>}

              <section aria-label="决策原因" className="my-4 rounded-xl bg-gray-50 p-4">
                <h3 className="mb-2 text-sm font-semibold text-gray-900">决策原因</h3>
                <div className="space-y-2 break-words text-sm leading-relaxed text-gray-700 [overflow-wrap:anywhere]">
                  {(view.reasons.length ? view.reasons : [view.reasonSummary]).map((reason) => <p key={reason}>{reason}</p>)}
                </div>
              </section>

              <section aria-label="信号与我的账户结果" className="grid grid-cols-1 gap-3 rounded-xl border border-gray-100 p-4 text-sm sm:grid-cols-2">
                <div>
                  <h3 className="text-xs text-gray-500">策略信号</h3>
                  <p className="mt-1 font-medium text-gray-900">{view.signalLabel}</p>
                </div>
                <div>
                  <h3 className="text-xs text-gray-500">我的账户执行</h3>
                  <p className="mt-1 font-medium text-gray-900">{view.myCopyLabel}</p>
                </div>
              </section>

              {!!item.my_accounts?.length && (
                <section className="mt-4" aria-label="我的账户结果">
                  <h3 className="text-xs font-semibold text-gray-700">我的账户</h3>
                  <div className="mt-2 space-y-2">
                    {item.my_accounts.map((account) => (
                      <div key={account.follow_relation_id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 px-3 py-2 text-xs">
                        <div className="min-w-0 break-words">
                          <p className="font-medium text-gray-800">{account.account_label}</p>
                          <p className="mt-1 text-gray-600">{copyResultLabel(account.execution_status)}</p>
                          {account.reason && <p className="mt-1 text-gray-500">{account.reason}</p>}
                        </div>
                        {item.signal_id && <Link className="shrink-0 text-indigo-600" to={`/execution-log?signal_id=${encodeURIComponent(item.signal_id)}`}>查看本账号结果</Link>}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <div className="mt-4 flex items-center justify-between gap-3 border-t border-gray-100 pt-3 text-xs text-gray-500">
                <span>{item.updated_at ? '记录更新' : '记录时间'}：{formatDateTime(item.updated_at || item.created_at) || '时间暂缺'}</span>
                {!id && <button className="shrink-0 text-indigo-600" onClick={() => openDecision(item.decision_id)}>查看详情</button>}
              </div>
            </article>
          );
        })}
      </div>

      {!id && (
        <div className="mt-4 flex gap-4 text-sm">
          <button disabled={page === 1 || busy} className="min-h-11 disabled:text-gray-400" onClick={() => {
            restored.current = true;
            setSearchParams({ page: String(page - 1) }, { replace: true, state: null });
          }}>上一页</button>
          <button disabled={!more || busy} className="min-h-11 disabled:text-gray-400" onClick={() => {
            restored.current = true;
            setSearchParams({ page: String(page + 1) }, { replace: true, state: null });
          }}>下一页</button>
        </div>
      )}
    </main>
  );
}
